import { cn, Avatar, AvatarFallback, AvatarImage } from '@clarix/ui';
import { getAvatarColor, getInitials } from './avatar-utils';

export interface UserAvatarProps {
  /** Display name — drives the initials and the deterministic fallback colour. */
  name?: string | null;
  /** Optional photo URL; falls back to coloured initials when absent or broken. */
  src?: string | null;
  /** Sizing/shape classes for the avatar circle (e.g. `'h-7 w-7'`). */
  className?: string;
  /** Extra classes for the initials fallback (e.g. a custom `text-sm`). */
  fallbackClassName?: string;
}

/**
 * Canonical single-user avatar: a photo when available, otherwise coloured
 * initials derived from `name` via `getInitials` + `getAvatarColor`. This is the
 * one home for the initials-avatar pattern that was previously hand-wired
 * (Avatar + AvatarFallback + getInitials + getAvatarColor) across member lists,
 * pickers, and table cells. Use `AvatarGroup` for stacks of multiple users.
 */
export function UserAvatar({ name, src, className, fallbackClassName }: UserAvatarProps) {
  const initials = getInitials(name);
  return (
    <Avatar className={className}>
      {src ? (
        // Radix mounts the image only after it loads → cross-fade the swap in
        // rather than a hard cut (self-disables under reduced motion).
        <AvatarImage
          src={src}
          alt={name ?? ''}
          className="motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-150"
        />
      ) : null}
      <AvatarFallback
        className={cn('font-medium text-white', getAvatarColor(initials), fallbackClassName)}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}
